import { type FormEvent, type MouseEvent, useEffect, useId, useRef, useState } from "react";
import defaultSite from "./content/site.json";
import { upgradeSiteContent } from "./content/upgrade";

type SiteContent = typeof defaultSite;
type Path = Array<string | number>;
type UploadState = {
  preview: string;
  name: string;
  status: "uploading" | "uploaded" | "error";
  error?: string;
};
type InstagramPost = { id: string; image: string; link: string; timestamp?: string };
type InstagramManagerPayload = { posts?: InstagramPost[]; hiddenLinks?: string[]; source?: string; fetchedAt?: string; error?: string };

const cloneDefault = () => JSON.parse(JSON.stringify(defaultSite)) as SiteContent;

function setAtPath(source: SiteContent, path: Path, value: string) {
  const next = JSON.parse(JSON.stringify(source)) as SiteContent;
  let cursor: any = next;
  path.slice(0, -1).forEach((part) => { cursor = cursor[part]; });
  cursor[path.at(-1)!] = value;
  return next;
}

function updateArrayAtPath(source: SiteContent, path: Path, update: (items: unknown[]) => unknown[]) {
  const next = JSON.parse(JSON.stringify(source)) as SiteContent;
  let cursor: any = next;
  path.slice(0, -1).forEach((part) => { cursor = cursor[part]; });
  const key = path.at(-1)!;
  cursor[key] = update(cursor[key]);
  return next;
}

const galleryPaths = new Set(["team.members", "philosophy.images"]);
const isGalleryItems = (path: Path) => galleryPaths.has(path.join("."));
const contentSections = ["hero", "philosophy", "menu", "team", "reservations", "contact", "navigation", "brand", "seo", "interface"] as const;
type ContentSectionKey = typeof contentSections[number];
const sectionOrder = [...contentSections, "instagram"] as const;
type SectionKey = typeof sectionOrder[number];
const sectionFromUrl = (): SectionKey => {
  const key = window.location.pathname.split("/")[2];
  return sectionOrder.find((section) => section === key) || "hero";
};
const sectionDescriptions: Record<SectionKey, string> = {
  hero: "設定首頁首屏的照片、標題與主要按鈕。",
  philosophy: "整理品牌故事與相簿，介紹 Asura 的泰國家常味。",
  menu: "更新招牌菜式的照片、名稱與介紹。",
  team: "編輯泰式風味的故事、人物與輪播照片。",
  reservations: "設定「聯絡我們」區塊顯示的分店位置標題。圖片與舊式按鈕欄位已移除。",
  contact: "更新整合區塊的標題、分店地址、聯絡方式和社交平台。",
  navigation: "編輯網站導覽選單的名稱與順序。",
  brand: "管理品牌標誌、背景圖樣與品牌名稱。",
  seo: "設定搜尋結果及分享連結顯示的標題、描述與圖片。",
  interface: "編輯網站的操作提示、語言切換和無障礙文字。",
  instagram: "抓取最新貼文，選擇哪些要出現在網站的 Instagram 區塊。",
};

function defaultArrayItem(path: Path) {
  const joined = path.join(".");
  if (joined === "philosophy.images") return defaultSite.philosophy.images[0];
  if (joined === "team.members") return defaultSite.team.members[0];
  return null;
}

function labelFor(part: string | number) {
  const labels: Record<string, string> = {
    seo: "SEO", brand: "品牌", interface: "介面文字", navigation: "導覽", instagram: "Instagram 貼文",
    hero: "首頁主視覺", philosophy: "品牌故事", menu: "招牌菜式",
    reservations: "分店位置標題", team: "泰式風味", contact: "聯絡資料",
    title: "標題", description: "描述", subtitle: "副標題", body: "內文",
    intro: "簡介", paragraphs: "段落", image: "圖片", alt: "圖片替代文字",
    href: "連結", cta: "按鈕文字", ctaHref: "按鈕連結", name: "名稱",
    role: "類別", bio: "內容", members: "項目", images: "照片", items: "菜式", columns: "分店",
    lines: "資料", social: "社交平台", legal: "頁尾說明", logo: "Logo",
    pattern: "背景圖樣", ariaLabel: "無障礙標籤", zh: "繁體中文", en: "English",
    skip: "跳至主要內容", language: "語言", more: "了解更多", dialogClose: "關閉內容",
    primaryNavigation: "主要導覽", secondaryNavigation: "次要導覽", mobileNavigation: "手機導覽",
    carouselLabel: "菜式輪播名稱", carouselHint: "菜式輪播提示", switchLanguage: "切換語言", mobileLanguage: "手機語言",
  };
  return typeof part === "number" ? `項目 ${part + 1}` : (labels[part] || part);
}

function ImageField({ value, path, onChange, onUpload, upload, busy }: {
  value: string;
  path: Path;
  onChange: (path: Path, value: string) => void;
  onUpload: (path: Path, file: File) => Promise<void>;
  upload?: UploadState;
  busy: boolean;
}) {
  const id = useId();
  const source = upload?.preview || value;
  const [failedSource, setFailedSource] = useState<string | null>(null);
  const uploading = upload?.status === "uploading";
  return (
    <div className="cms-image-field" data-field-path={path.join(".")}>
      <div className="cms-image-tools" aria-busy={uploading}>
        <div className="cms-image-preview">
          {source && source !== failedSource ? (
            <a href={source} target="_blank" rel="noopener noreferrer" aria-label="在新視窗查看圖片">
              <img src={source} alt={`${labelFor(path.at(-1) ?? "image")}預覽`} onError={() => setFailedSource(source)} />
              <span className="cms-image-preview__zoom">查看大圖 ↗</span>
            </a>
          ) : <span className="cms-image-preview__empty">{source ? "圖片未能載入，請重新選擇圖片。" : "尚未選擇圖片"}</span>}
        </div>
        <div className="cms-image-upload">
          <p className="cms-image-upload__title">{labelFor(path.at(-1) ?? "image")}</p>
          <p className="cms-image-upload__name">{upload?.name || "選擇圖片後，預覽會立即顯示。"}</p>
          <label className={`cms-upload-button ${busy ? "is-disabled" : ""}`} htmlFor={`${id}-file`}>
            {uploading ? "正在上傳…" : "選擇／更換圖片"}
          </label>
          <input id={`${id}-file`} className="cms-file-input" type="file" disabled={busy}
            accept="image/jpeg,image/png,image/webp,image/gif"
            aria-describedby={`${id}-hint`}
            onChange={(event) => {
              const file = event.currentTarget.files?.[0];
              event.currentTarget.value = "";
              if (file) void onUpload(path, file);
            }} />
          <p id={`${id}-hint`} className="cms-field-hint">JPG、PNG、WebP 或 GIF · 最大 5 MB</p>
          {upload && <p className={`cms-upload-status cms-upload-status--${upload.status}`} role="status">
            {uploading ? "已選取圖片，正在上傳…" : upload.status === "uploaded" ? "✓ 上傳完成，圖片已套用至草稿。" : `上傳失敗：${upload.error}`}
          </p>}
        </div>
      </div>
      <label className="cms-field" htmlFor={`${id}-url`}>
        <span>圖片網址</span>
        <input id={`${id}-url`} value={value} disabled={busy} onChange={(event) => onChange(path, event.target.value)} />
      </label>
    </div>
  );
}

function FieldTree({
  value,
  path,
  onChange,
  onUpload,
  onArrayAdd,
  onArrayRemove,
  uploads,
  busy,
}: {
  value: unknown;
  path: Path;
  onChange: (path: Path, value: string) => void;
  onUpload: (path: Path, file: File) => Promise<void>;
  onArrayAdd: (path: Path) => void;
  onArrayRemove: (path: Path, index: number) => void;
  uploads: Record<string, UploadState>;
  busy: boolean;
}) {
  const key = path.at(-1);

  if (typeof value === "string") {
    const isImage = ["image", "logo", "pattern"].includes(String(key));
    if (isImage) return <ImageField value={value} path={path} onChange={onChange} onUpload={onUpload} upload={uploads[path.join(".")]} busy={busy} />;
    const longText = value.length > 70 || ["description", "bio", "body", "intro", "legal"].includes(String(key));
    return (
      <label className="cms-field" data-field-path={path.join(".")}>
        <span>{labelFor(key ?? "")}</span>
        {longText ? (
          <textarea rows={Math.min(7, Math.max(2, Math.ceil(value.length / 58)))} value={value}
            onChange={(event) => onChange(path, event.target.value)} />
        ) : (
          <input value={value} onChange={(event) => onChange(path, event.target.value)} />
        )}
      </label>
    );
  }

  if (Array.isArray(value)) {
    return (
      <div className="cms-array">
        {value.map((item, index) => (
          <fieldset className="cms-group" key={index}>
            <legend>{labelFor(index)}</legend>
            <FieldTree value={item} path={[...path, index]} onChange={onChange} onUpload={onUpload}
              onArrayAdd={onArrayAdd} onArrayRemove={onArrayRemove} uploads={uploads} busy={busy} />
            {isGalleryItems(path) && value.length > 1 && (
              <button className="cms-array__remove" type="button" disabled={busy} onClick={() => onArrayRemove(path, index)}>
                移除此照片
              </button>
            )}
          </fieldset>
        ))}
        {isGalleryItems(path) && (
          <button className="cms-array__add" type="button" disabled={busy} onClick={() => onArrayAdd(path)}>
            ＋ 新增照片
          </button>
        )}
      </div>
    );
  }

  if (value && typeof value === "object") {
    return (
      <div className="cms-object">
        {Object.entries(value).map(([childKey, childValue]) => (
          <div className={`cms-node ${childValue && typeof childValue === "object" && !Array.isArray(childValue) && "zh" in childValue && "en" in childValue ? "cms-node--localized" : ""}`} key={childKey}>
            {typeof childValue === "object" && childValue !== null
              ? <h4>{labelFor(childKey)}</h4>
              : null}
            <FieldTree value={childValue} path={[...path, childKey]} onChange={onChange} onUpload={onUpload}
              onArrayAdd={onArrayAdd} onArrayRemove={onArrayRemove} uploads={uploads} busy={busy} />
          </div>
        ))}
      </div>
    );
  }

  return null;
}

function InstagramManager({ active, onDirtyChange }: { active: boolean; onDirtyChange: (dirty: boolean) => void }) {
  const [posts, setPosts] = useState<InstagramPost[]>([]);
  const [hiddenLinks, setHiddenLinks] = useState<string[]>([]);
  const [savedHiddenLinks, setSavedHiddenLinks] = useState<string[]>([]);
  const [source, setSource] = useState("empty");
  const [fetchedAt, setFetchedAt] = useState("");
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");
  const [failedImages, setFailedImages] = useState<string[]>([]);
  const dirty = JSON.stringify([...hiddenLinks].sort()) !== JSON.stringify([...savedHiddenLinks].sort());
  const visibleCount = posts.filter((post) => !hiddenLinks.includes(post.link)).length;

  useEffect(() => { onDirtyChange(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => {
    let mounted = true;
    fetch("/api/cms/instagram", { credentials: "same-origin", cache: "no-store" })
      .then(async (response) => {
        const payload = await response.json() as InstagramManagerPayload;
        if (!response.ok) throw new Error(payload.error || "無法載入 Instagram 顯示設定");
        if (!mounted) return;
        const saved = payload.hiddenLinks || [];
        setPosts(payload.posts || []);
        setHiddenLinks(saved);
        setSavedHiddenLinks(saved);
        setSource(payload.source || "empty");
        setFetchedAt(payload.fetchedAt || "");
      })
      .catch((error) => { if (mounted) setMessage(error instanceof Error ? error.message : "無法載入 Instagram 顯示設定"); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const refresh = async () => {
    setWorking(true);
    setMessage("");
    try {
      const response = await fetch("/api/cms/instagram", { method: "POST", credentials: "same-origin" });
      const payload = await response.json() as InstagramManagerPayload;
      if (!response.ok) throw new Error(payload.error || "無法抓取最新貼文");
      setPosts(payload.posts || []);
      setSource("instagram");
      setFetchedAt(payload.fetchedAt || "");
      setFailedImages([]);
      setMessage(`已抓取 ${payload.posts?.length || 0} 則貼文。選好後請儲存顯示設定。`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "無法抓取最新貼文");
    } finally {
      setWorking(false);
    }
  };

  const save = async () => {
    setWorking(true);
    setMessage("");
    try {
      const response = await fetch("/api/cms/instagram", {
        method: "PUT", credentials: "same-origin", headers: { "content-type": "application/json" },
        body: JSON.stringify({ hiddenLinks }),
      });
      const payload = await response.json() as InstagramManagerPayload;
      if (!response.ok) throw new Error(payload.error || "無法儲存顯示設定");
      setSavedHiddenLinks(payload.hiddenLinks || []);
      setMessage("顯示設定已儲存，網站會在短時間內更新。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "無法儲存顯示設定");
    } finally {
      setWorking(false);
    }
  };

  const sourceLabel = source === "instagram" ? "最新抓取" : source === "instagram-cache" ? "上次抓取" : source === "instagram-snapshot" ? "網站備份" : "尚未抓取";
  const fetchedLabel = fetchedAt && !Number.isNaN(Date.parse(fetchedAt))
    ? new Intl.DateTimeFormat("zh-HK", { dateStyle: "medium", timeStyle: "short" }).format(new Date(fetchedAt)) : "";

  return (
    <section className="cms-section cms-instagram-page" hidden={!active} aria-label="Instagram 貼文顯示設定">
      <div className="cms-instagram-tools">
        <div>
          <span className="cms-instagram-source">{sourceLabel}{fetchedLabel ? ` · ${fetchedLabel}` : ""}</span>
          <p>新貼文預設顯示。可逐則隱藏；網站最多顯示最新的 12 則已選貼文。</p>
        </div>
        <button type="button" className="cms-instagram-refresh" onClick={refresh} disabled={loading || working}>
          {working ? "處理中…" : "抓取最新貼文"}
        </button>
      </div>
      <div className="cms-instagram-summary">
        <strong>{visibleCount}</strong><span>則已選擇顯示</span>
        <span className="cms-instagram-summary__note">{visibleCount > 12 ? "首頁會顯示其中最新的 12 則" : "首頁最多顯示 12 則"}</span>
      </div>
      {message && <p className="cms-instagram-message" role="status">{message}</p>}
      {loading ? <p className="cms-instagram-empty">正在載入貼文設定…</p> : posts.length ? (
        <div className="cms-instagram-grid">
          {posts.map((post, index) => {
            const visible = !hiddenLinks.includes(post.link);
            const date = post.timestamp && !Number.isNaN(Date.parse(post.timestamp))
              ? new Intl.DateTimeFormat("zh-HK", { year: "numeric", month: "short", day: "numeric" }).format(new Date(post.timestamp)) : "";
            return (
              <article className={`cms-instagram-card ${visible ? "is-visible" : "is-hidden"}`} key={post.link}>
                <div className="cms-instagram-card__image">
                  {failedImages.includes(post.link)
                    ? <span>預覽暫時無法載入</span>
                    : <img src={post.image} alt={`Instagram 貼文 ${index + 1} 預覽`} referrerPolicy="no-referrer"
                        onError={() => setFailedImages((current) => [...current, post.link])} />}
                </div>
                <div className="cms-instagram-card__details">
                  <span className="cms-instagram-card__meta">{post.link.includes("/reel/") ? "Reel" : "貼文"}{date ? ` · ${date}` : ""}</span>
                  <label className="cms-instagram-toggle">
                    <input type="checkbox" checked={visible} disabled={working}
                      onChange={(event) => setHiddenLinks((current) => event.target.checked
                        ? current.filter((link) => link !== post.link)
                        : [...new Set([...current, post.link])])} />
                    <span>在網站顯示</span>
                  </label>
                  <a href={post.link} target="_blank" rel="noopener noreferrer">在 Instagram 查看</a>
                </div>
              </article>
            );
          })}
        </div>
      ) : <p className="cms-instagram-empty">目前沒有可選擇的貼文。按「抓取最新貼文」開始。</p>}
      <div className="cms-instagram-actions">
        <span role="status">{dirty ? "顯示設定尚未儲存" : "顯示設定已儲存"}</span>
        <button type="button" className="button button--accent" onClick={save} disabled={loading || working || !dirty}>
          儲存顯示設定
        </button>
      </div>
    </section>
  );
}

export default function CmsApp() {
  const [draft, setDraft] = useState<SiteContent>(cloneDefault);
  const [published, setPublished] = useState<SiteContent>(cloneDefault);
  const [activeSection, setActiveSection] = useState<SectionKey>(sectionFromUrl);
  const [uploads, setUploads] = useState<Record<string, UploadState>>({});
  const previewUrls = useRef(new Map<string, string>());
  const pageHeading = useRef<HTMLHeadingElement>(null);
  const pageIndex = useRef<HTMLElement>(null);
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [passwordPanel, setPasswordPanel] = useState(false);
  const [passwordChange, setPasswordChange] = useState({ current: "", next: "", confirm: "" });
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [instagramDirty, setInstagramDirty] = useState(false);
  const [removeUnusedImages, setRemoveUnusedImages] = useState(() => window.localStorage.getItem("asura-cms-remove-unused-images") !== "false");
  const dirtySections = contentSections.filter((key) => JSON.stringify(draft[key]) !== JSON.stringify(published[key]));

  useEffect(() => {
    window.localStorage.setItem("asura-cms-remove-unused-images", String(removeUnusedImages));
  }, [removeUnusedImages]);

  useEffect(() => {
    const syncPage = () => setActiveSection(sectionFromUrl());
    window.addEventListener("popstate", syncPage);
    if (window.location.pathname !== `/cms/${sectionFromUrl()}`) {
      window.history.replaceState(null, "", `/cms/${sectionFromUrl()}`);
    }
    const urls = previewUrls.current;
    return () => {
      window.removeEventListener("popstate", syncPage);
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  useEffect(() => {
    document.title = `${labelFor(activeSection)} · Asura CMS`;
    if (authenticated) {
      window.scrollTo({ top: 0, behavior: "instant" });
      pageHeading.current?.focus({ preventScroll: true });
      if (window.matchMedia("(max-width: 980px)").matches) {
        pageIndex.current?.querySelector('[aria-current="page"]')?.scrollIntoView({ block: "nearest", inline: "center", behavior: "instant" });
      }
    }
  }, [activeSection, authenticated]);

  useEffect(() => {
    if (!dirtySections.length && !instagramDirty && !busy) return;
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [dirtySections.length, instagramDirty, busy]);

  const navigate = (event: MouseEvent<HTMLAnchorElement>, key: SectionKey) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (key === activeSection) return;
    window.history.pushState(null, "", `/cms/${key}`);
    setActiveSection(key);
  };

  const clearUploads = (path: Path) => {
    const prefix = path.join(".");
    setUploads((current) => Object.fromEntries(Object.entries(current).filter(([key]) => prefix && key !== prefix && !key.startsWith(`${prefix}.`))));
    for (const [key, url] of previewUrls.current) {
      if (!prefix || key === prefix || key.startsWith(`${prefix}.`)) {
        URL.revokeObjectURL(url);
        previewUrls.current.delete(key);
      }
    }
  };

  const load = async () => {
    const response = await fetch("/api/cms/content", { headers: { accept: "application/json" } });
    if (response.status === 401) {
      setAuthenticated(false);
      return;
    }
    if (!response.ok) throw new Error("CMS 暫時無法使用");
    const payload = await response.json() as { content?: SiteContent | null };
    const content = upgradeSiteContent(payload.content || cloneDefault());
    setDraft(content);
    setPublished(content);
    setAuthenticated(true);
  };

  useEffect(() => {
    load().catch((error) => {
      setMessage(error instanceof Error ? error.message : "CMS 暫時無法使用");
      setAuthenticated(false);
    });
  }, []);

  const login = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const response = await fetch("/api/cms/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const payload = await response.json().catch(() => ({})) as { error?: string };
      if (!response.ok) throw new Error(payload.error || "登入失敗");
      setPassword("");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "登入失敗");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (busy) return;
    setBusy(true);
    setMessage("");
    try {
      const response = await fetch("/api/cms/content", {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ content: draft, removeUnusedImages }),
      });
      const payload = await response.json().catch(() => ({})) as { error?: string; removedImages?: number; cleanupFailed?: number };
      if (!response.ok) throw new Error(payload.error || "儲存失敗");
      setPublished(draft);
      const removed = payload.removedImages || 0;
      const failed = payload.cleanupFailed || 0;
      setMessage(`所有變更已儲存並發佈。${removed ? `已移除 ${removed} 張未使用的舊照片。` : ""}${failed ? `有 ${failed} 張舊照片未能移除。` : ""}網站會在短時間內更新。`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "儲存失敗");
    } finally {
      setBusy(false);
    }
  };

  const upload = async (path: Path, file: File) => {
    if (busy) return;
    const key = path.join(".");
    clearUploads(path);
    const preview = URL.createObjectURL(file);
    previewUrls.current.set(key, preview);
    setUploads((current) => ({ ...current, [key]: { preview, name: file.name, status: "uploading" } }));
    setBusy(true);
    setMessage("正在上傳圖片…");
    try {
      if (!file.size) throw new Error("圖片是空的，請重新選擇。");
      if (file.size > 5_000_000) throw new Error("圖片大於 5 MB，請縮小後重試。");
      if (!/\.(jpe?g|png|webp|gif)$/i.test(file.name) && !["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) {
        throw new Error("請使用 JPG、PNG、WebP 或 GIF；HEIC 圖片請先轉成 JPG。");
      }
      const response = await fetch("/api/cms/assets", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": file.type || "application/octet-stream" },
        body: file,
      });
      const payload = await response.json().catch(() => ({})) as { error?: string; url?: string };
      if (!response.ok || !payload.url) throw new Error(payload.error || "圖片上傳失敗");
      setDraft((current) => setAtPath(current, path, payload.url!));
      setUploads((current) => ({ ...current, [key]: { preview, name: file.name, status: "uploaded" } }));
      setMessage("圖片已上傳並加入草稿，請按「儲存全部變更」發佈。");
    } catch (error) {
      const detail = error instanceof Error ? error.message : "圖片上傳失敗";
      URL.revokeObjectURL(preview);
      previewUrls.current.delete(key);
      setUploads((current) => ({ ...current, [key]: { preview: "", name: file.name, status: "error", error: detail } }));
      setMessage(detail);
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await fetch("/api/cms/logout", { method: "POST" }).catch(() => undefined);
    setAuthenticated(false);
    clearUploads([]);
  };

  const changePassword = async (event: FormEvent) => {
    event.preventDefault();
    setPasswordMessage("");
    if (passwordChange.next !== passwordChange.confirm) {
      setPasswordMessage("兩次輸入的新密碼不一致。");
      return;
    }
    setPasswordBusy(true);
    try {
      const response = await fetch("/api/cms/password", {
        method: "PUT",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ currentPassword: passwordChange.current, newPassword: passwordChange.next }),
      });
      const payload = await response.json().catch(() => ({})) as { error?: string };
      if (!response.ok) throw new Error(payload.error || "修改密碼失敗");
      setPasswordChange({ current: "", next: "", confirm: "" });
      setPasswordPanel(false);
      setMessage("管理密碼已更新。其他已登入的裝置需要使用新密碼重新登入。");
    } catch (error) {
      setPasswordMessage(error instanceof Error ? error.message : "修改密碼失敗");
    } finally {
      setPasswordBusy(false);
    }
  };

  if (authenticated === null) {
    return <main className="cms-shell"><p>正在載入 CMS…</p></main>;
  }

  if (!authenticated) {
    return (
      <main className="cms-shell cms-login">
        <div className="cms-login__layout">
          <div className="cms-login__brand">
            <img src={defaultSite.brand.logo} alt="Asura" width="88" height="88" />
            <div>
              <p className="cms-kicker">ASURA · CONTENT STUDIO</p>
              <h1>把每一頁，<br />都照顧好。</h1>
              <p className="cms-login__intro">在這裡整理品牌故事、菜式與分店資訊，讓網站保持最新狀態。</p>
            </div>
            <span className="cms-login__footnote">ASURA THAI · HONG KONG</span>
          </div>
          <form className="cms-login__card" onSubmit={login}>
            <p className="cms-kicker">WELCOME BACK</p>
            <h2>登入內容管理</h2>
            <p className="cms-login__hint">輸入管理密碼，繼續編輯網站內容。</p>
            <label className="cms-field">
              <span>管理密碼</span>
              <input type="password" autoComplete="current-password" value={password}
                onChange={(event) => setPassword(event.target.value)} required />
            </label>
            <button className="button button--accent" type="submit" disabled={busy}>進入工作室 <span aria-hidden="true">→</span></button>
            {message && <p className="cms-message" role="status">{message}</p>}
            <a className="cms-login__back" href="/">← 返回網站</a>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className={`cms-shell ${activeSection === "instagram" ? "cms-shell--instagram" : ""}`}>
      <header className="cms-toolbar">
        <div className="cms-toolbar__identity">
          <img src={defaultSite.brand.logo} alt="" width="54" height="54" />
          <div><p className="cms-kicker">ASURA · CONTENT STUDIO</p><h1>內容管理</h1></div>
        </div>
        <div className="cms-toolbar__actions">
          <a className="cms-secondary" href="/" target="_blank" rel="noopener noreferrer">預覽網站</a>
          <button className="cms-secondary cms-password-trigger" type="button" onClick={() => { setPasswordPanel(true); setPasswordMessage(""); }}>修改密碼</button>
          <button className="cms-secondary" type="button" onClick={logout}>登出</button>
          {(activeSection !== "instagram" || dirtySections.length > 0) &&
            <button className="button button--accent" type="button" onClick={save} disabled={busy || !dirtySections.length}>{busy ? "處理中…" : "儲存全部變更"}</button>}
        </div>
      </header>
      <div className="cms-workspace">
        <nav className="cms-index" ref={pageIndex} aria-label="CMS 頁面">
          <p className="cms-index__label">網站章節</p>
          {sectionOrder.map((key, index) => (
            <a href={`/cms/${key}`} key={key} onClick={(event) => navigate(event, key)} aria-current={activeSection === key ? "page" : undefined}>
              <span>{String(index + 1).padStart(2, "0")}</span>{labelFor(key)}
              {(key === "instagram" ? instagramDirty : dirtySections.includes(key as ContentSectionKey)) && <i className="cms-index__dirty" aria-label="未儲存" />}
            </a>
          ))}
          <p className="cms-index__note">切換頁面會保留草稿。完成後請儲存全部變更。</p>
        </nav>
        <div className="cms-sections" aria-labelledby="cms-page-title">
          <div className="cms-sections__intro">
            <p className="cms-kicker">CONTENT / {String(sectionOrder.indexOf(activeSection) + 1).padStart(2, "0")}</p>
            <h2 id="cms-page-title" ref={pageHeading} tabIndex={-1}>{labelFor(activeSection)}</h2>
            <p>{sectionDescriptions[activeSection]}</p>
            <span className={`cms-save-state ${activeSection === "instagram" ? (instagramDirty ? "has-changes" : "") : (dirtySections.length ? "has-changes" : "")}`} role="status">
              {activeSection === "instagram" ? (instagramDirty ? "Instagram 顯示設定尚未儲存" : "Instagram 顯示設定已儲存")
                : dirtySections.length ? `${dirtySections.length} 個頁面有未儲存變更` : "所有變更已儲存"}
            </span>
            {activeSection !== "instagram" && <label className="cms-cleanup-setting">
              <input type="checkbox" checked={removeUnusedImages} disabled={busy}
                onChange={(event) => setRemoveUnusedImages(event.target.checked)} />
              <span><strong>儲存後移除舊照片</strong><small>只移除 CMS 上傳且不再被任何頁面使用的照片；網站內建圖片會保留。</small></span>
            </label>}
          </div>
          {message && <p className="cms-message" role="status">{message}</p>}
          {activeSection !== "instagram" && <section className="cms-section" key={activeSection} data-section={activeSection} aria-label={`${labelFor(activeSection)}編輯欄位`}>
              <FieldTree
                value={draft[activeSection]}
                path={[activeSection]}
                uploads={uploads}
                busy={busy}
                onChange={(path, value) => {
                  clearUploads(path);
                  setDraft((current) => setAtPath(current, path, value));
                }}
                onUpload={upload}
                onArrayAdd={(path) => setDraft((current) => updateArrayAtPath(current, path, (items) => {
                  const template = items.at(-1) ?? defaultArrayItem(path);
                  if (!template) return items;
                  return [...items, JSON.parse(JSON.stringify(template))];
                }))}
                onArrayRemove={(path, itemIndex) => {
                  clearUploads(path);
                  setDraft((current) => updateArrayAtPath(
                    current,
                    path,
                    (items) => items.length > 1 ? items.filter((_, index) => index !== itemIndex) : items,
                  ));
                }}
              />
          </section>}
          <InstagramManager active={activeSection === "instagram"} onDirtyChange={setInstagramDirty} />
        </div>
      </div>
      {(activeSection !== "instagram" || dirtySections.length > 0) && <div className="cms-mobile-actions">
        <button className="button button--accent" type="button" onClick={save} disabled={busy || !dirtySections.length}>{busy ? "處理中…" : "儲存全部變更"}</button>
      </div>}
      {passwordPanel && (
        <div className="cms-dialog-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget && !passwordBusy) setPasswordPanel(false);
        }}>
          <section className="cms-password-dialog" role="dialog" aria-modal="true" aria-labelledby="cms-password-title">
            <button className="cms-dialog-close" type="button" aria-label="關閉修改密碼" disabled={passwordBusy}
              onClick={() => setPasswordPanel(false)}>×</button>
            <p className="cms-kicker">SECURITY</p>
            <h2 id="cms-password-title">修改管理密碼</h2>
            <p className="cms-password-dialog__intro">更新後會保留此裝置的登入狀態，其他裝置需要使用新密碼重新登入。</p>
            <form onSubmit={changePassword}>
              <label className="cms-field">
                <span>目前密碼</span>
                <input type="password" autoComplete="current-password" value={passwordChange.current} required
                  onChange={(event) => setPasswordChange((current) => ({ ...current, current: event.target.value }))} />
              </label>
              <label className="cms-field">
                <span>新密碼</span>
                <input type="password" autoComplete="new-password" minLength={10} maxLength={128} value={passwordChange.next} required
                  onChange={(event) => setPasswordChange((current) => ({ ...current, next: event.target.value }))} />
                <small>最少 10 個字元。</small>
              </label>
              <label className="cms-field">
                <span>再次輸入新密碼</span>
                <input type="password" autoComplete="new-password" minLength={10} maxLength={128} value={passwordChange.confirm} required
                  onChange={(event) => setPasswordChange((current) => ({ ...current, confirm: event.target.value }))} />
              </label>
              {passwordMessage && <p className="cms-password-error" role="alert">{passwordMessage}</p>}
              <button className="button button--accent" type="submit" disabled={passwordBusy}>{passwordBusy ? "正在更新…" : "更新密碼"}</button>
            </form>
          </section>
        </div>
      )}
    </main>
  );
}
